import { useEffect, useRef, useState } from "react";
import { useInView } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";
import { Reveal } from "./Reveal";
import { SectionHead } from "./SectionHead";
import { CtaBand } from "./CtaBand";

const list = (v) => (Array.isArray(v) ? v : []);
const Paras = ({ items, dark }) => list(items).map((p) => (
  <p key={p} className={`text-base leading-relaxed md:text-lg ${dark ? "text-violet-100" : "text-slate-600"}`}>{p}</p>
));

export const StoryMetrics = ({ c }) => {
  const many = list(c.metrics).length > 3;
  return (
  <section className="relative z-10 -mt-10" data-testid="cs-top-metrics">
    <div className={`mx-auto grid max-w-7xl gap-3 px-5 sm:px-8 ${many ? "grid-cols-2 lg:grid-cols-3" : "sm:grid-cols-3"}`}>
      {list(c.metrics).map((m, i) => (
        <Reveal key={m.label} delay={many ? 0 : i * 0.08} y={many ? 12 : 28} className="rounded-[24px] border border-violet-100 bg-white p-5 shadow-[0_30px_60px_-40px_rgba(46,16,101,.6)] sm:p-7">
          <p className={`font-extrabold tracking-tight text-[#581C87] ${many ? "text-3xl sm:text-4xl" : "text-4xl sm:text-5xl"}`} data-testid={`cs-metric-value-${i}`}>{m.value}</p>
          <p className="mt-2 text-sm font-semibold text-slate-600" data-testid={`cs-metric-label-${i}`}>{m.label}</p>
        </Reveal>
      ))}
    </div>
  </section>
  );
};

export const StoryChallenge = ({ c }) => (
  <section className="bg-white py-20 sm:py-28" data-testid="cs-challenge">
    <div className="mx-auto grid max-w-7xl gap-10 px-5 sm:px-8 lg:grid-cols-[1fr_1.3fr]">
      <SectionHead eyebrow={`${c.industry} · ${c.location}`} title="The Challenge" />
      <Reveal className="space-y-4"><Paras items={c.challenge} /></Reveal>
    </div>
  </section>
);

export const TypeLine = ({ text, delay, className = "font-semibold text-[#13052A]", caret = "bg-[#FF7043]" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView || n >= text.length) return undefined;
    const t = setTimeout(() => setN((v) => v + 1), n === 0 ? delay : 45);
    return () => clearTimeout(t);
  }, [inView, n, text, delay]);
  return (
    <span ref={ref} className={`relative inline-block ${className}`} aria-label={text} data-testid="cs-typed-term">
      <span className="invisible" aria-hidden>{text}</span>
      <span className="absolute inset-0 whitespace-nowrap" aria-hidden>
        {text.slice(0, n)}
        {n < text.length && <span className={`type-caret ml-0.5 inline-block h-[0.95em] w-[2px] translate-y-[0.12em] ${caret}`} />}
      </span>
    </span>
  );
};

export const StoryApproach = ({ c }) => (
  <section className="bg-[#F5F0FF] py-20 sm:py-28" data-testid="cs-approach">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <SectionHead eyebrow={c.approach_eyebrow || "Meta advertising strategy"} title="Our Approach" />
      <Reveal className="mt-6 max-w-3xl space-y-4"><Paras items={c.approach_intro} /></Reveal>
      <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {list(c.approach).map((a, i) => (
          <Reveal key={a.title} delay={(i % 3) * 0.06} className="flex h-full flex-col rounded-[24px] bg-white p-7" data-testid={`cs-approach-${i}`}>
            <span className="text-xs font-bold uppercase tracking-widest text-[#7C3AED]">0{i + 1}</span>
            <h3 className="mt-3 text-xl font-extrabold text-[#13052A]">{a.title}</h3>
            <div className="mt-3 space-y-3 text-sm leading-relaxed text-slate-600">{list(a.paras).map((p) => <p key={p}>{p}</p>)}</div>
            {list(a.points).length > 0 && (
              <ul className="mt-3 space-y-2">{list(a.points).map((p, k) => <li key={p} className="flex items-start gap-2 text-sm text-slate-700"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#7C3AED]" />{a.type_points ? <TypeLine text={p} delay={400 + k * 1400} /> : p}</li>)}</ul>
            )}
            {list(a.after).length > 0 && <div className="mt-3 space-y-3 text-sm leading-relaxed text-slate-600">{list(a.after).map((p) => <p key={p}>{p}</p>)}</div>}
            {a.link && <ApproachLink l={a.link} i={i} />}
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const ApproachLink = ({ l, i }) => {
  const cls = `${l.external ? "btn-primary" : "text-sm font-bold text-[#7C3AED] hover:text-[#581C87]"} mt-auto inline-flex items-center gap-1.5 self-start pt-5`;
  const inner = <>{l.label} <ArrowUpRight className="h-4 w-4" /></>;
  return l.external
    ? <a href={l.href} target="_blank" rel="noopener noreferrer" className={cls} data-testid={`cs-approach-link-${i}`}>{inner}</a>
    : <Link to={l.href} className={cls} data-testid={`cs-approach-link-${i}`}>{inner}</Link>;
};

export const StoryServices = ({ c }) => (
  <section className="bg-white py-20 sm:py-24" data-testid="cs-services">
    <div className="mx-auto grid max-w-7xl gap-10 px-5 sm:px-8 lg:grid-cols-[1fr_1.3fr]">
      <SectionHead eyebrow="What we did" title="Services Delivered" text={c.services_intro} />
      <Reveal>
        <ul className="grid gap-3 sm:grid-cols-2">
          {list(c.services).flatMap((s) => list(s.points)).map((p) => (
            <li key={p} className="flex items-start gap-2.5 rounded-2xl bg-[#F5F0FF] p-4 text-sm font-semibold text-[#13052A]"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#7C3AED]" />{p}</li>
          ))}
        </ul>
        <Link to={c.services_link_to || c.cta_to} className="btn-ghost mt-6" data-testid="cs-services-link">{c.services_link_label || "Facebook & Instagram Ads in Kent and Tunbridge Wells"} <ArrowUpRight className="h-4 w-4" /></Link>
      </Reveal>
    </div>
  </section>
);

export const StoryOutcome = ({ c }) => (
  <section className="grain relative overflow-hidden bg-[#13052A] py-20 text-white sm:py-28" data-testid="cs-results">
    <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
      <SectionHead dark eyebrow={`${c.results_title ? "Reported results" : "Attributed results"} · ${c.campaign_period}`} title={c.results_title || "The Outcome"} />
      {list(c.result_points).length > 0 && (
        <ul className="mt-10 grid gap-3 sm:grid-cols-2" data-testid="cs-result-points">
          {list(c.result_points).map((r) => <li key={r} className="flex items-start gap-3 text-base text-violet-50"><CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#60A5FA]" />{r}</li>)}
        </ul>
      )}
      {c.results_note && c.outcome_heading && <p className="mt-6 max-w-3xl text-sm text-violet-200" data-testid="cs-results-note">{c.results_note}</p>}
      {list(c.periods).length > 0 && <div className="mt-10 grid gap-3 sm:grid-cols-2">
        {list(c.periods).map((p, i) => (
          <Reveal key={p.label} delay={i * 0.08} className="rounded-[24px] bg-white/5 p-6 ring-1 ring-white/10 sm:p-7" data-testid={`cs-period-${i}`}>
            <h3 className="text-[11px] font-bold uppercase tracking-widest text-violet-200">{p.label}</h3>
            <p className="mt-3 text-3xl font-extrabold sm:text-4xl">{p.revenue}</p>
            <p className="mt-1 text-sm text-violet-100">{p.purchases} purchases · {p.note}</p>
          </Reveal>
        ))}
      </div>}
      {c.outcome_heading && <h2 className="mt-16 text-2xl font-extrabold sm:text-3xl" data-testid="cs-outcome-heading">{c.outcome_heading}</h2>}
      <Reveal className={`${c.outcome_heading ? "mt-5" : "mt-10"} max-w-3xl space-y-4`}><Paras items={c.outcome} dark /></Reveal>
      {c.results_note && !c.outcome_heading && <p className="mt-6 max-w-3xl text-sm text-violet-200" data-testid="cs-results-note">{c.results_note}</p>}
    </div>
  </section>
);

export const StoryTakeaway = ({ c }) => (
  <section className="bg-white py-20 sm:py-28" data-testid="cs-takeaway">
    <div className="mx-auto max-w-4xl px-5 sm:px-8">
      <SectionHead eyebrow="Key lesson" title="The Takeaway" />
      <Reveal className="mt-6 space-y-4"><Paras items={c.takeaway} /></Reveal>
      {c.tagline && <p className="serif-accent mt-8 text-2xl text-[#581C87] sm:text-3xl" data-testid="cs-tagline">{c.tagline}</p>}
    </div>
  </section>
);

export const StoryCta = ({ c }) => {
  const f = c.final_cta || {};
  return (
    <CtaBand title={f.title} text={f.text}>
      <Link to={f.primary_to} className="btn-accent" data-testid="cs-final-cta-primary">{f.primary_label} <ArrowUpRight className="h-4 w-4" /></Link>
      <Link to={f.secondary_to} className="btn-ghost-dark" data-testid="cs-final-cta-contact">{f.secondary_label} <ArrowUpRight className="h-4 w-4" /></Link>
    </CtaBand>
  );
};
